import { useDepartmentText } from 'model/DepartmentTerminology';
import { Alert, Box, Loader, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { HrGetTermResponse } from 'api/actions/hr-get-term/hr-get-term-response';
import { HrListTermHistoryResponse } from 'api/actions/hr-list-term-history/hr-list-term-history-response';
import { useApi } from 'api/api-context';
import FilePreview from 'components/files/FilePreview';
import FormInputGroup from 'components/forms/FormInputGroup';
import HrExecuteTermModal from 'components/modals/hr-execute-term/HrExecuteTermModal';
import HrPairCertificateModal from 'components/modals/hr-pair-certificate/HrPairCertificateModal';
import HrReactivateTermModal from 'components/modals/hr-reactivate-term/HrReactivateTermModal';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { HrTermActions } from 'components/tables/data/hr/HrActions';
import H3Medium from 'components/typography/H3Medium';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import ReadonlyText from 'components/typography/ReadonlyText';
import panic from 'errors/panic';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import {
  formatHrAuditAction,
  formatHrDeadlineState,
  formatHrRecurrence,
  formatHrStatus,
  formatHrTermKind,
} from 'pages/hr-module/hr-formatters';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HR_TERMS_PAGE_PATH } from 'routes/paths';

type HrTermHistoryItem = HrListTermHistoryResponse[number];
type HrTerm = HrGetTermResponse & { organizationName?: string };

/** Formats an API date with the same locale treatment used by revision details. */
function formatDate(value?: string, withTime = false) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return withTime ? date.toLocaleString('sk') : date.toLocaleDateString('sk');
}

/** Displays an immutable HR history note. */
function HrTermHistoryNote({ item }: { item: HrTermHistoryItem }) {
  if (!item.note) {
    return <Box data-testid="hr-history-empty-note-cell" />;
  }

  return <P2Regular style={{ whiteSpace: 'pre-line' }}>{item.note}</P2Regular>;
}

/** Displays the authorized attachments associated with one immutable HR history row. */
function HrTermHistoryAttachments({ item }: { item: HrTermHistoryItem }) {
  const proofFiles = (item.proofFiles ?? []).filter((file) => file.fileId);

  if (proofFiles.length === 0) {
    return <Box data-testid="hr-history-empty-attachments-cell" />;
  }

  return (
    <Stack spacing={8}>
      {proofFiles.map((file) => (
        <FilePreview
          key={file.personFileId ?? file.fileId}
          fileId={file.fileId!}
          label={file.displayName ?? file.originalName ?? file.categoryName ?? 'Príloha'}
          hideDelete
          showSlider={false}
        />
      ))}
    </Stack>
  );
}

/** Read-only HR term detail with revision-style basic information and change log. */
export default function HrTermPage() {
  const departmentText = useDepartmentText();
  const { termId } = useParams();
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { isMobile, isTablet } = useResponsiveDesign();
  const theme = useMantineTheme();
  const [term, setTerm] = useState<HrTerm | null>(null);
  const [history, setHistory] = useState<HrListTermHistoryResponse>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [executeTerm, setExecuteTerm] = useState<HrTerm | null>(null);
  const [pairTerm, setPairTerm] = useState<HrTerm | null>(null);
  const [reactivateTerm, setReactivateTerm] = useState<HrTerm | null>(null);
  const [fileCategories, setFileCategories] = useState<any[]>([]);

  useEffect(() => {
    if (!termId) {
      setError('Termín neexistuje.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    Promise.all([
      getAction('HrGetTerm')({ parameters: { termId } }),
      getAction('HrListTermHistory')({ parameters: { termId } }),
    ])
      .then(([nextTerm, nextHistory]) => {
        setTerm(nextTerm);
        setHistory(nextHistory);
      })
      .catch((caught: any) => {
        const status = caught?.response?.status;
        setError(
          status === 403
            ? 'Nemáte oprávnenie zobraziť tento termín.'
            : status === 404
            ? 'Termín neexistuje.'
            : 'Termín sa nepodarilo načítať.'
        );
        if (status !== 403 && status !== 404) {
          panic(caught);
        }
      })
      .finally(() => setLoading(false));
  }, [getAction, refreshToken, termId]);

  const archiveTerm = useCallback(
    (selected: HrTerm) => {
      confirm({
        title: 'Vyradiť termín',
        content: `Naozaj si prajete vyradiť termín „${selected.termTypeName}“? História zostane zachovaná.`,
        primaryButtonText: 'Vyradiť',
        secondaryButtonText: 'Zrušiť',
        onConfirm: async () => {
          try {
            await getAction('HrSetTermStatus')({
              parameters: { termId: String(selected.termId) },
              payload: { version: selected.version!, status: 'archived' },
            });
            setRefreshToken((value) => value + 1);
          } catch (caught) {
            panic(caught as Error);
          }
        },
      });
    },
    [confirm, getAction]
  );

  const openExecuteTerm = useCallback(
    async (selected: HrTerm) => {
      setFileCategories([]);
      setExecuteTerm(selected);
      if (selected.actionPermissions?.accessFiles && selected.organizationId) {
        try {
          setFileCategories(
            await getAction('HrListFileCategories')({
              query: { filters: { organizationId: selected.organizationId, status: 'active' } },
            })
          );
        } catch (caught) {
          panic(caught as Error);
        }
      }
    },
    [getAction]
  );

  const unpairCertificate = useCallback(
    (selected: HrTerm) => {
      confirm({
        title: 'Odpojiť osvedčenie',
        content: 'Termín sa vyradí, osvedčenie a jeho história zostanú zachované.',
        primaryButtonText: 'Odpojiť',
        secondaryButtonText: 'Zrušiť',
        onConfirm: async () => {
          try {
            await getAction('HrUnpairCertificate')({ parameters: { termId: String(selected.termId) } });
            setRefreshToken((value) => value + 1);
          } catch (caught) {
            panic(caught as Error);
          }
        },
      });
    },
    [confirm, getAction]
  );

  const personName = [term?.firstName, term?.lastName].filter(Boolean).join(' ');

  return (
    <DashboardLayout
      title={`Detail HR termínu: ${term?.termTypeName ?? ''}`}
      breadcrumbs={[
        { title: 'Termíny', link: HR_TERMS_PAGE_PATH.original },
        { title: term?.termTypeName ?? 'Načítavam...' },
      ]}
      actions={
        term && (
          <HrTermActions
            data={term}
            onArchive={archiveTerm}
            onExecute={openExecuteTerm}
            onReactivate={setReactivateTerm}
            showDetail={false}
            hideDetailDestination
            onPairCertificate={setPairTerm}
            onUnpairCertificate={unpairCertificate}
          />
        )
      }
    >
      {loading && (
        <Stack align="center" py={80}>
          <Loader aria-label="Načítavam detail HR termínu" />
        </Stack>
      )}

      {!loading && error && <Alert color="red">{error}</Alert>}

      {!loading && term && !error && (
        <Stack spacing={24}>
          <FormInputGroup
            groupTitle="Základné informácie"
            stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
          >
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Typ termínu" value={term.termTypeName ?? ''} />
              <ReadonlyText title="ID" value={term.termId ? String(term.termId) : ''} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Osoba" value={personName} />
              <ReadonlyText title="Organizácia" value={term.organizationName ?? ''} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title={departmentText('Položka úrovne', 'Stredisko')} value={term.departmentName ?? ''} />
              <ReadonlyText title="Druh" value={formatHrTermKind(term.kind)} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Najbližší termín" value={formatDate(term.nextDueDate)} />
              <ReadonlyText title="Opakovanie" value={formatHrRecurrence(term.recurrence)} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Stav" value={formatHrStatus(term.status)} />
              <ReadonlyText title="Stav lehoty" value={formatHrDeadlineState(term.deadlineState, term.status)} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Vytvorené" value={formatDate(term.createdAt, true)} />
              <ReadonlyText title="Aktualizované" value={formatDate(term.updatedAt, true)} />
            </SimpleGrid>
            {term.kind === 'technician-certificate' && (
              <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                <ReadonlyText title="Číslo osvedčenia" value={term.certificateNumber ?? ''} />
                <ReadonlyText title="Dátum obnovy" value={formatDate(term.renewalDate)} />
              </SimpleGrid>
            )}
            <ReadonlyText title="Poznámka" value={term.note ?? ''} />
          </FormInputGroup>

          <Stack pt={24} pb={40} px={isTablet ? 20 : 40} bg="white" style={{ borderRadius: '4px' }} spacing={24}>
            <H3Medium color="gray.7">Záznamy</H3Medium>
            {isMobile ? (
              <Stack spacing={0}>
                {history.map((item, index) => (
                  <Stack
                    key={item.auditLogId ?? item.executionId ?? item.id ?? index}
                    spacing={12}
                    py={16}
                    px={16}
                    sx={(currentTheme) => ({ borderBottom: `1px solid ${currentTheme.colors.gray[1]}` })}
                  >
                    <ReadonlyText title="Udalosť" value={formatHrAuditAction(item.action ?? item.itemType)} />
                    <ReadonlyText
                      title="Používateľ"
                      value={item.actorFullName || (item.actorUserId ? `ID ${item.actorUserId}` : '')}
                    />
                    <ReadonlyText title="Dátum" value={formatDate(item.occurredAt ?? item.executedOn, true)} />
                    <ReadonlyText title="Poznámka" value={<HrTermHistoryNote item={item} />} />
                    <ReadonlyText title="Prílohy" value={<HrTermHistoryAttachments item={item} />} />
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Stack spacing={0}>
                <Box className="grid grid-cols-[18%_12%_17%_23%_30%] rounded-[4px]" p={16} bg="gray.0">
                  <P1Medium>Udalosť</P1Medium>
                  <P1Medium>Používateľ</P1Medium>
                  <P1Medium>Dátum</P1Medium>
                  <P1Medium>Poznámka</P1Medium>
                  <P1Medium>Prílohy</P1Medium>
                </Box>
                {history.map((item, index) => (
                  <Box
                    py={24}
                    px={16}
                    key={item.auditLogId ?? item.executionId ?? item.id ?? index}
                    style={{ borderBottom: `1px solid ${theme.colors.gray[1]}`, alignItems: 'flex-start' }}
                    className="grid grid-cols-[18%_12%_17%_23%_30%]"
                  >
                    <P2Regular>{formatHrAuditAction(item.action ?? item.itemType)}</P2Regular>
                    <P2Regular>{item.actorFullName || (item.actorUserId ? `ID ${item.actorUserId}` : null)}</P2Regular>
                    <P2Regular>{formatDate(item.occurredAt ?? item.executedOn, true)}</P2Regular>
                    <HrTermHistoryNote item={item} />
                    <HrTermHistoryAttachments item={item} />
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      )}

      {executeTerm && (
        <HrExecuteTermModal
          term={executeTerm}
          fileCategories={fileCategories}
          opened
          onClose={() => setExecuteTerm(null)}
          onExecuted={() => setRefreshToken((value) => value + 1)}
          onConflict={() => {
            setExecuteTerm(null);
            setRefreshToken((value) => value + 1);
          }}
        />
      )}
      <HrPairCertificateModal
        term={pairTerm}
        onClose={() => setPairTerm(null)}
        onPaired={() => setRefreshToken((value) => value + 1)}
      />

      <HrReactivateTermModal
        term={reactivateTerm}
        onClose={() => setReactivateTerm(null)}
        onReactivated={() => setRefreshToken((value) => value + 1)}
        onConflict={() => {
          setReactivateTerm(null);
          setRefreshToken((value) => value + 1);
          notifications.show({
            title: 'Údaje sa medzičasom zmenili',
            message: 'Detail bol obnovený. Otvorte zaradenie termínu znova.',
            color: 'orange',
          });
        }}
      />
    </DashboardLayout>
  );
}
